// src/store/six-detail/thunks/deleteFolderThunk.ts
import { createAppAsyncThunk } from "@/store/thunks/createAppAsyncThunk";
import { mapSixFilesDTOToSixFiles, mapSixFoldersDTOToSixFolders } from "@/store/mappers/mapSixDetail";
import type { SixFile, SixFolder } from "@/types/six/Six";
import type { SixFilesApiResponse } from "@/types/six/SixDTOs";
import { withErrorHandling, withLoader } from "@/store/thunks/thunkWrappers";

interface DeleteFolderParams {
  sixId: string;
  folderPath: string;
}

interface DeleteFolderResult {
  files: SixFile[];
  folders: SixFolder[];
}

/**
 * Deletes a folder from a Six (backend handles cascade deletion of contents).
 * HttpFetcherService auto-fetches the resourceUrl after CQRS polling,
 * returning the full files/folders list directly.
 *
 * @param sixId - The Six containing the folder
 * @param folderPath - The path of the folder to delete
 */
export const deleteFolder = createAppAsyncThunk<DeleteFolderResult, DeleteFolderParams>(
  "sixDetail/deleteFolder",
  withErrorHandling(
    withLoader(async({ sixId, folderPath }, { extra }) => {
      // CQRS polling + auto-fetch returns the full files response
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- auto-fetch returns SixFilesApiResponse
      await extra.sixDetailService.deleteFolder(sixId, folderPath) as unknown as SixFilesApiResponse;
      const filesResponse = await extra.sixDetailService.fetchFiles(sixId);

      const files = mapSixFilesDTOToSixFiles(filesResponse.files);
      const folders = mapSixFoldersDTOToSixFolders(filesResponse.folders);

      return { files, folders };
    })
  )
);
